/**
 * Grounding projection — walk the boat's straight-line track forward and find
 * the first place it would cross into water too shallow to float in. Unlike the
 * cone lookahead (which lists dangers in an arc), this answers the urgent
 * question: "on this exact heading and speed, how long until we touch?".
 *
 * Depth is supplied by a callback so this stays pure and testable; the caller
 * wires it to the charted depth under a point (chartedDepthUnderBoat). With a
 * draught and a height of tide we test the actual under-keel clearance; without
 * them we fall back to the cautious chart-datum reading against the safety depth.
 */
import { rhumbDestination } from '@/nav';

export interface GroundingInput {
  position: [number, number];
  cogDeg: number;
  sogKn: number;
  /** Charted depth (m, relative to chart datum) at a point, or null if unknown. */
  depthAt: (pos: [number, number]) => number | null;
  /** Alarm threshold against the charted depth when no draught is given, metres. */
  safetyDepthM: number;
  /** Boat draught, metres; if given we test under-keel clearance instead. */
  draughtM?: number;
  /** Height of tide now, metres (added to charted depth). */
  tideM?: number;
  /** How far ahead to look, minutes (default 12). */
  horizonMin?: number;
  /** Sampling step along the track, nm (default 0.02). */
  stepNm?: number;
}

export interface GroundingProjection {
  /** True if the projected track crosses unsafe water within the horizon. */
  willGround: boolean;
  /** The first unsafe point ahead, [lon, lat], or null. */
  point: [number, number] | null;
  /** Distance to that point, nm, or null. */
  distanceNm: number | null;
  /** Time to that point at the current SOG, minutes, or null. */
  etaMin: number | null;
  /** Under-keel clearance there (m) if a draught was given, else charted depth. */
  clearanceM: number | null;
}

const SAFE: GroundingProjection = {
  willGround: false,
  point: null,
  distanceNm: null,
  etaMin: null,
  clearanceM: null,
};

export function projectGrounding(input: GroundingInput): GroundingProjection {
  const { position, cogDeg, sogKn, depthAt, safetyDepthM } = input;
  if (sogKn <= 0.1) return SAFE; // not making way: nothing to project
  const horizonMin = input.horizonMin ?? 12;
  const stepNm = input.stepNm ?? 0.02;
  const horizonNm = sogKn * (horizonMin / 60);
  const tide = input.tideM ?? 0;
  const draught = input.draughtM;

  for (let d = stepNm; d <= horizonNm + 1e-9; d += stepNm) {
    const p = rhumbDestination(position, cogDeg, d);
    const charted = depthAt(p);
    if (charted == null) continue;
    const clearance = charted + tide - (draught ?? 0);
    const unsafe = draught != null ? clearance <= 0 : charted < safetyDepthM;
    if (unsafe) {
      return {
        willGround: true,
        point: p,
        distanceNm: d,
        etaMin: (d / sogKn) * 60,
        clearanceM: draught != null ? clearance : charted,
      };
    }
  }
  return SAFE;
}
